package uz.mint.frontend.components.sections

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.css.functions.clamp
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.navigation.UncoloredLinkVariant
import com.varabyte.kobweb.silk.components.navigation.UndecoratedLinkVariant
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import uz.mint.frontend.PageGutter
import uz.mint.frontend.ShellStyle
import uz.mint.frontend.api.Customer
import uz.mint.frontend.api.SessionState
import uz.mint.frontend.api.rememberSession
import uz.mint.frontend.alignSelf
import uz.mint.frontend.i18n.AccountStrings
import uz.mint.frontend.i18n.Locale
import uz.mint.frontend.i18n.PrivacyPolicy
import uz.mint.frontend.i18n.PublicOffer
import uz.mint.frontend.i18n.accountStringsFor
import uz.mint.frontend.i18n.stringsFor
import uz.mint.frontend.toSitePalette

/**
 * Page shell for the signed-in area, plus the gate in front of it.
 *
 * The gate is the reason this exists rather than each page repeating itself. These pages are
 * **statically exported like every other route**, so the HTML a visitor receives knows nothing
 * about them: the session is resolved by a request that has not happened yet when the first
 * frame renders. Treating that moment as "logged out" would flash a login prompt at every
 * signed-in customer on every page load, so [SessionState.Loading] gets its own quiet state.
 *
 * @param content receives the customer, so pages below never have to re-check.
 */
@Composable
fun AccountShell(
    locale: Locale,
    title: String,
    lede: String? = null,
    content: @Composable (Customer, AccountStrings) -> Unit,
) {
    val strings = stringsFor(locale)
    val account = accountStringsFor(locale)
    val palette = ColorMode.current.toSitePalette()
    val session = rememberSession()
    var legal by remember { mutableStateOf<LegalDoc?>(null) }

    Column(Modifier.fillMaxWidth()) {
        NavHeader(
            locale = locale,
            strings = strings,
            anchorBase = locale.homePath,
            localePath = { it.profilePath },
        )

        Div(
            ShellStyle.toModifier()
                .padding(top = clamp(32.px, 3.4.vw, 56.px), bottom = clamp(48.px, 5.vw, 84.px), leftRight = PageGutter)
                .toAttrs()
        ) {
            Column(Modifier.fillMaxWidth().gap(28.px)) {
                SpanText(
                    title,
                    Modifier
                        .fontSize(clamp(28.px, 4.vw, 44.px))
                        .fontWeight(FontWeight.ExtraBold)
                        .letterSpacing((-0.03).em)
                        .lineHeight(1.05),
                )
                lede?.let {
                    SpanText(it, Modifier.fontSize(16.px).color(palette.ink2).maxWidth(620.px))
                }

                when (session) {
                    SessionState.Loading -> SpanText(
                        account.gateLoading,
                        Modifier.fontSize(15.px).color(palette.ink2),
                    )

                    SessionState.Anonymous -> AuthGate(locale, account)

                    is SessionState.SignedIn -> content(session.user, account)
                }
            }
        }

        Footer(
            locale = locale,
            strings = strings,
        )
    }

    when (legal) {
        LegalDoc.OFFER -> LegalOverlay(strings.legalOffer, PublicOffer, strings) { legal = null }
        LegalDoc.PRIVACY -> LegalOverlay(strings.legalPrivacy, PrivacyPolicy, strings) { legal = null }
        null -> Unit
    }
}

/**
 * Shown instead of the page body when nobody is signed in.
 *
 * A link to `/login`, not a redirect: a bounce would erase the URL the customer meant to reach
 * and, on a static export, could fire before the session lookup even finished.
 */
@Composable
private fun AuthGate(locale: Locale, account: AccountStrings) {
    val palette = ColorMode.current.toSitePalette()

    Column(
        Modifier
            .fillMaxWidth()
            .maxWidth(520.px)
            .border(2.px, LineStyle.Solid, palette.rule)
            .backgroundColor(palette.card)
            .padding(28.px)
            .gap(14.px)
    ) {
        SpanText(account.gateTitle, Modifier.fontSize(20.px).fontWeight(FontWeight.ExtraBold))
        SpanText(account.gateBody, Modifier.fontSize(15.px).color(palette.ink2))
        Link(
            path = locale.loginPath,
            modifier = Modifier
                .margin(top = 6.px)
                .alignSelf("flex-start")
                .backgroundColor(palette.solidBg)
                .color(palette.solidFg)
                .fontWeight(FontWeight.Bold)
                .fontSize(15.px)
                .padding(topBottom = 13.px, leftRight = 22.px),
            variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
        ) { SpanText(account.gateCta) }
    }
}

/**
 * Section card used by every panel in the account area.
 *
 * The design has no account screens, so these follow the same rules as everything else it does
 * define: 2px rules, a flat card fill and no shadows.
 */
@Composable
fun AccountCard(title: String? = null, lede: String? = null, content: @Composable () -> Unit) {
    val palette = ColorMode.current.toSitePalette()

    Column(
        Modifier
            .fillMaxWidth()
            .border(2.px, LineStyle.Solid, palette.rule)
            .backgroundColor(palette.card)
            .padding(clamp(20.px, 2.4.vw, 28.px))
            .gap(16.px)
    ) {
        title?.let {
            SpanText(it, Modifier.fontSize(19.px).fontWeight(FontWeight.ExtraBold).letterSpacing((-0.02).em))
        }
        lede?.let { SpanText(it, Modifier.fontSize(14.px).color(palette.ink2)) }
        content()
    }
}
